import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Collapse, Flex, Text } from '@chakra-ui/react';

import { FolderIcon, FolderOpenIcon } from 'components/custom-icons';

import { DropDownOption } from '../type';

// eslint-disable-next-line import/no-cycle
import RenderOptionList from './RenderOptionList';

interface SmartRouteDropDownOption extends DropDownOption {
  type?: string;
  children?: any[];
}

interface Props {
  option: SmartRouteDropDownOption;
  value: string;
  onChange: (data: string) => void;
  subSection?: number;
  isTruncated?: boolean;
}

// Check if a group contains the selected value
const containsValue = (children: SmartRouteDropDownOption[], value: string): boolean => {
  if (!children || !value) return false;

  return children.some((child) => {
    if (child.value === value) return true;
    if (child.children) return containsValue(child.children, value);
    return false;
  });
};

const RenderGroupOptions = ({ option, value, onChange, subSection = 0, isTruncated }: Props) => {
  // Check if this group contains the selected value
  const shouldAutoExpand = containsValue(option.children || [], value);
  const [isOpen, setIsOpen] = useState(shouldAutoExpand);

  // Update isOpen when value changes (in case selection changes)
  useEffect(() => {
    if (value && containsValue(option.children || [], value)) {
      setIsOpen(true);
    }
  }, [value, option.children]);

  const isFolder = option.type === 'folder';
  const isMessagesGroup = option.type === 'messages-group';
  const isQuestionsGroup = option.type === 'questions-group';
  const isApplicationsGroup = option.type === 'applications-group';

  const getBorderColor = () => {
    if (isMessagesGroup) return 'blue.400';
    if (isQuestionsGroup) return 'orange.400';
    if (isApplicationsGroup) return 'green.400';
    return undefined;
  };

  return (
    <Box>
      <Flex
        bg="gray.100"
        p={1}
        px={2}
        mt={1}
        ml={subSection || 0}
        borderRadius="md"
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        onClick={() => setIsOpen((prevState) => !prevState)}
        borderLeft={getBorderColor() ? '4px solid' : undefined}
        borderLeftColor={getBorderColor()}
      >
        <Flex alignItems="center" gap={2} flex="1">
          {isFolder && (isOpen ? <FolderOpenIcon /> : <FolderIcon />)}
          <Text
            whiteSpace="break-spaces"
            fontSize="sm"
            fontWeight={
              isMessagesGroup || isQuestionsGroup || isApplicationsGroup ? 'bold' : 'medium'
            }
          >
            {option.label}
          </Text>
        </Flex>
        {isOpen ? <ChevronRightIcon /> : <ChevronDownIcon />}
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box>
          <RenderOptionList
            options={option.children}
            value={value}
            onChange={onChange}
            subSection={subSection + 4}
            isTruncated={isTruncated}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default RenderGroupOptions;
